import type { ReactNode } from "react";
import { Portal, Tooltip as ChakraTooltip } from "@chakra-ui/react";

/**
 * A tooltip, composed the way Chakra documents it.
 *
 * The project had no snippet for this, and a `title` attribute is not enough
 * where the content is more than a few words: the browser's own tooltip
 * cannot be styled, cannot hold a second line reliably, and takes a second to
 * appear. This one is used where a control is too small to carry its own
 * label — the cells of the progress matrix, above all.
 *
 * `content` is a node rather than a string so a caller can give a heading and
 * a status on separate lines.
 */
export function Tooltip({
  content,
  children,
  disabled = false,
  openDelay = 120,
  closeDelay = 60,
}: {
  content: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  openDelay?: number;
  closeDelay?: number;
}) {
  if (disabled) {
    return <>{children}</>;
  }
  return (
    <ChakraTooltip.Root openDelay={openDelay} closeDelay={closeDelay}>
      <ChakraTooltip.Trigger asChild>{children}</ChakraTooltip.Trigger>
      <Portal>
        <ChakraTooltip.Positioner>
          <ChakraTooltip.Content maxW="64" px={3} py={2} fontSize="xs" lineHeight="1.5">
            <ChakraTooltip.Arrow>
              <ChakraTooltip.ArrowTip />
            </ChakraTooltip.Arrow>
            {content}
          </ChakraTooltip.Content>
        </ChakraTooltip.Positioner>
      </Portal>
    </ChakraTooltip.Root>
  );
}
